import { useEffect, useState, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, CalendarIcon, Search, X } from 'lucide-react';
import { BLUE } from '../../constants';
import { useClickAway } from '../../hooks/useClickAway';
import { emptyJournalFilters, JournalFiltersDropdown, type JournalFilters } from './JournalFiltersDropdown';
import { coerceMarketTypeToVisible, getVisibleMarketTypes, shouldShowAssetClassSelector, useAssetClassVisibility } from '../../lib/assetClassVisibility';

export type TimeframeValue = 'alltime' | 'last24h' | '7days' | '30days' | 'custom';
export type SideFilterValue = 'All' | 'Long' | 'Short';

export type TopFiltersApplyPayload = {
  timeframe: TimeframeValue;
  fromDate?: string;
  toDate?: string;
  sideFilter: SideFilterValue;
  searchTerm: string;
  advancedFilters: JournalFilters;
};

const pad2 = (n: number) => String(n).padStart(2, '0');
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function DatePickerField({
  value,
  onChange,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}): JSX.Element {
  const openPicker = () => {
    const node = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!node) return;
    try {
      node.showPicker?.();
      node.focus();
    } catch {
      node.focus();
      node.click();
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef as any}
        type="date"
        aria-label="Date"
        className="tt-date-input h-10 w-full rounded-lg border border-zinc-300 dark:border-zinc-700 px-2 pr-9 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Open date picker"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-zinc-700 dark:text-white"
      >
        <CalendarIcon size={16} />
      </button>
    </div>
  );
}

export function TopFilters({
  marketFilter,
  onChangeMarket,
  initialTimeframe = 'alltime',
  initialFromDate,
  initialToDate,
  initialSideFilter = 'All',
  initialSearchTerm = '',
  initialAdvancedFilters,
  onApply,
  onChange,
  showApplyButton = true,
}: {
  marketFilter?: 'ALL'|'STOCKS'|'FUTURES'|'FOREX'|'CRYPTO';
  onChangeMarket?: (m: 'ALL'|'STOCKS'|'FUTURES'|'FOREX'|'CRYPTO') => void;
  initialTimeframe?: TimeframeValue;
  initialFromDate?: string;
  initialToDate?: string;
  initialSideFilter?: SideFilterValue;
  initialSearchTerm?: string;
  initialAdvancedFilters?: JournalFilters;
  onApply?: (payload: TopFiltersApplyPayload) => void;
  onChange?: (payload: TopFiltersApplyPayload) => void;
  showApplyButton?: boolean;
} = {}) {
  const [assetVisibility] = useAssetClassVisibility();
  const [timeframe, setTimeframe] = useState<TimeframeValue>(initialTimeframe);
  const [fromDate, setFromDate] = useState<string | undefined>(() => {
    if (typeof initialFromDate !== 'undefined') return initialFromDate;
    if (initialTimeframe === 'last24h') {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 1);
      return toISODate(from);
    }
    if (initialTimeframe === '7days') {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return toISODate(from);
    }
    if (initialTimeframe === '30days') {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return toISODate(from);
    }
    // 'alltime' or 'custom' without explicit initialFromDate -> undefined
    return undefined;
  });
  const [toDate, setToDate] = useState<string | undefined>(() => {
    if (typeof initialToDate !== 'undefined') return initialToDate;
    if (initialTimeframe === 'alltime') return undefined;
    return toISODate(new Date());
  });
  const [openRange, setOpenRange] = useState(false);
  const [sideFilter, setSideFilter] = useState<SideFilterValue>(initialSideFilter);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [advancedFilters, setAdvancedFilters] = useState<JournalFilters>(() => initialAdvancedFilters ?? emptyJournalFilters());

  const visibleMarketOptions = getVisibleMarketTypes(assetVisibility).map((marketType) => ({
    key: marketType,
    label: marketType,
  }));

  useEffect(() => {
    if (!marketFilter || !onChangeMarket) return;
    const safe = coerceMarketTypeToVisible(marketFilter, assetVisibility, { fallbackToAll: true });
    if (safe !== marketFilter) onChangeMarket(safe);
  }, [assetVisibility, marketFilter, onChangeMarket]);

  const rangeRef = useRef<HTMLDivElement>(null);
  const rangeButtonRef = useRef<HTMLButtonElement>(null);
  const rangePopoverRef = useRef<HTMLDivElement>(null);
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const [rangePopoverPos, setRangePopoverPos] = useState<{ top: number; left: number } | null>(null);

  useClickAway([rangeRef, rangePopoverRef], () => setOpenRange(false));

  useEffect(() => {
    if (!openRange) return;

    const updatePosition = () => {
      const rect = rangeButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setRangePopoverPos({
        top: rect.bottom + 8,
        left: rect.left,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openRange]);

  useEffect(() => {
    if (!initialAdvancedFilters) return;
    setAdvancedFilters(initialAdvancedFilters);
  }, [initialAdvancedFilters]);

  useEffect(() => {
    setSearchTerm(initialSearchTerm);
  }, [initialSearchTerm]);

  const isCompleteIsoDate = (value?: string) => {
    const v = String(value ?? '').trim();
    if (!v) return true;
    return /^\d{4}-\d{2}-\d{2}$/.test(v);
  };

  useEffect(() => {
    const canEmitCustom = timeframe !== 'custom' || (isCompleteIsoDate(fromDate) && isCompleteIsoDate(toDate));
    if (!canEmitCustom) return;
    try {
      onChange?.({
        timeframe,
        fromDate,
        toDate,
        sideFilter,
        searchTerm,
        advancedFilters,
      });
    } catch {}
  }, [timeframe, fromDate, toDate, sideFilter, searchTerm, advancedFilters, onChange]);

  const clearFilters = () => {
    setFromDate(undefined);
    setToDate(undefined);
  };

  const applyQuickTimeframe = (value: Exclude<TimeframeValue, 'custom'>) => {
    setTimeframe(value);
    const now = new Date();
    const to = toISODate(now);
    const from = new Date(now);
    if (value === 'alltime') {
      setFromDate(undefined);
      setToDate(undefined);
      setOpenRange(false);
      return;
    }
    if (value === 'last24h') from.setDate(from.getDate() - 1);
    if (value === '7days') from.setDate(from.getDate() - 7);
    if (value === '30days') from.setDate(from.getDate() - 30);
    setFromDate(toISODate(from));
    setToDate(to);
    setOpenRange(false);
  };

  const timeframeDisplay =
    timeframe === 'alltime'
      ? 'All time'
      : timeframe === 'last24h'
        ? 'Last 24h'
        : timeframe === '7days'
          ? '7days'
          : timeframe === '30days'
            ? '30days'
            : fromDate && toDate
              ? 'Custom'
              : 'Custom...';

  const handleApply = () => {
    setOpenRange(false);
    try {
      onApply?.({
        timeframe,
        fromDate,
        toDate,
        sideFilter,
        searchTerm,
        advancedFilters,
      });
    } catch {}
  };

  const showMarketFilter = shouldShowAssetClassSelector(assetVisibility);

  return (
    <div className="relative mb-2 min-h-[55px] rounded-lg border border-zinc-300/90 bg-zinc-50 p-2 shadow-[0_18px_48px_-32px_rgba(15,23,42,0.55)] dark:border-zinc-700/90 dark:bg-zinc-900/95">
      <div className={`no-scrollbar grid min-w-0 items-center gap-2 overflow-x-auto ${showMarketFilter ? 'grid-cols-[auto_minmax(90px,420px)_minmax(0,1fr)_auto_auto] min-[700px]:grid-cols-[auto_minmax(90px,420px)_minmax(380px,1fr)_auto_auto]' : 'grid-cols-[auto_minmax(90px,420px)_auto_auto]'}`}>
        <div ref={rangeRef} className="relative flex-shrink-0">
          <button
            ref={rangeButtonRef}
            onClick={() => setOpenRange((v) => !v)}
            className="flex h-9 items-center gap-2 rounded-md border border-zinc-200/80 bg-white px-3 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-zinc-700/80 dark:bg-zinc-800/90 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <CalendarIcon size={16} className="shrink-0 text-zinc-500 dark:text-zinc-300" />
            <span className="whitespace-nowrap">{timeframeDisplay}</span>
            <ChevronDown size={14} className="shrink-0 text-zinc-400" />
          </button>
          {openRange && rangePopoverPos && createPortal(
            <div
              ref={rangePopoverRef}
              className="fixed z-[10050] w-[320px] rounded-xl border border-zinc-300 bg-white p-3 shadow-lg animate-dropdown dark:border-zinc-700 dark:bg-zinc-800"
              style={{ top: rangePopoverPos.top, left: rangePopoverPos.left, overflow: 'visible' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Timeframe</div>
                <button type="button" aria-label="Close timeframe picker" onClick={() => setOpenRange(false)} className="p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700">
                  <X size={16} />
                </button>
              </div>

              <div className="grid gap-1">
                <button
                  type="button"
                  onClick={() => applyQuickTimeframe('alltime')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 ${timeframe === 'alltime' ? 'bg-zinc-100 dark:bg-zinc-700' : ''}`}
                >
                  All time
                </button>
                <button
                  type="button"
                  onClick={() => applyQuickTimeframe('last24h')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 ${timeframe === 'last24h' ? 'bg-zinc-100 dark:bg-zinc-700' : ''}`}
                >
                  Last 24h
                </button>
                <button
                  type="button"
                  onClick={() => applyQuickTimeframe('7days')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 ${timeframe === '7days' ? 'bg-zinc-100 dark:bg-zinc-700' : ''}`}
                >
                  7days
                </button>
                <button
                  type="button"
                  onClick={() => applyQuickTimeframe('30days')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 ${timeframe === '30days' ? 'bg-zinc-100 dark:bg-zinc-700' : ''}`}
                >
                  30days
                </button>
                <button
                  type="button"
                  onClick={() => setTimeframe('custom')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 ${timeframe === 'custom' ? 'bg-zinc-100 dark:bg-zinc-700' : ''}`}
                >
                  Custom...
                </button>
              </div>

              {timeframe === 'custom' && (
                <>
                  <div className="h-px bg-zinc-200 dark:bg-zinc-700 my-3" />

                  <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Select range</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm text-zinc-600 dark:text-zinc-400">From</div>
                    <div className="text-sm text-zinc-600 dark:text-zinc-400">To</div>
                    <DatePickerField value={fromDate || ''} onChange={setFromDate} inputRef={fromInputRef} />
                    <DatePickerField value={toDate || ''} onChange={setToDate} inputRef={toInputRef} />
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button onClick={() => { clearFilters(); setOpenRange(false) }} className="px-3 h-9 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">Clear</button>
                    <button onClick={() => setOpenRange(false)} className="px-3 h-9 rounded-lg text-white" style={{ backgroundColor: BLUE }}>Set</button>
                  </div>
                </>
              )}
            </div>,
            document.body
          )}
        </div>

        <div className="w-full min-w-0">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
            <input
              aria-label="Search trades"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Symbol, TradeId, Tag, or Note"
              className="h-9 w-full rounded-md border border-zinc-200/80 bg-white pl-11 pr-4 text-sm font-normal text-zinc-700 outline-none transition-colors placeholder:text-zinc-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700/80 dark:bg-zinc-800/90 dark:text-zinc-200 dark:placeholder:text-zinc-500 dark:focus:border-blue-500/70"
            />
          </div>
        </div>

        {onChangeMarket && showMarketFilter && (
          <div className="flex min-w-0 items-center justify-center overflow-x-auto">
            <div className="w-full max-w-[500px] min-w-0 overflow-x-auto">
              <div className="flex h-9 w-full min-w-0 flex-nowrap items-center justify-center gap-0 overflow-hidden rounded-md border border-zinc-200/80 bg-white p-0 dark:border-zinc-700/80 dark:bg-zinc-800/80">
              {(visibleMarketOptions as readonly { key: string; label: string }[]).map((m, idx) => (
                <div key={m.key} className="inline-flex min-w-0 flex-1 items-center">
                  {idx !== 0 ? <span className="h-5 w-px bg-zinc-200 dark:bg-zinc-700/60" aria-hidden /> : null}
                  <button
                    type="button"
                    onClick={() => onChangeMarket?.(m.key as any)}
                    className={`flex h-9 w-[44px] min-w-0 flex-1 items-center justify-center rounded-none px-0.5 text-[9px] font-normal uppercase tracking-[-0.03em] transition-all min-[700px]:w-[54px] min-[700px]:px-1 min-[700px]:text-[13px] min-[700px]:tracking-[0.1em] xl:w-[92px] xl:flex-shrink-0 xl:px-3.5 xl:text-sm xl:tracking-[0.1em] ${marketFilter === (m.key as any)
                      ? `bg-blue-600 text-white shadow-[0_10px_24px_-16px_rgba(37,99,235,0.95)] ${idx === 0 ? 'rounded-l-md' : ''} ${idx === visibleMarketOptions.length - 1 ? 'rounded-r-md' : ''}`
                      : 'text-zinc-600 hover:bg-white hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-700/90 dark:hover:text-white'}`}
                  >
                    {m.label}
                  </button>
                </div>
              ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="relative">
            <select
              aria-label="Side filter"
              value={sideFilter}
              onChange={(e) => setSideFilter(e.target.value as any)}
              className="h-9 max-w-[76px] appearance-none rounded-md border border-zinc-200/80 bg-white pl-2 pr-6 text-xs font-medium text-zinc-700 outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 sm:max-w-none sm:pl-3 sm:pr-8 sm:text-sm dark:border-zinc-700/80 dark:bg-zinc-800/90 dark:text-zinc-200 dark:focus:border-blue-500/70"
            >
              <option>All</option>
              <option>Long</option>
              <option>Short</option>
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 sm:right-3" />
          </div>
        {showApplyButton ? (
          <button
            onClick={handleApply}
            className="flex-shrink-0 flex h-9 items-center justify-center rounded-md bg-blue-600 px-4 text-sm font-semibold leading-none text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Apply
          </button>
        ) : null}
        </div>

        <div className="flex-shrink-0">
          <JournalFiltersDropdown
            label="Advanced"
            value={advancedFilters}
            onApply={setAdvancedFilters}
            triggerClassName="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-200/80 bg-white pl-3 pr-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-zinc-700/80 dark:bg-zinc-800/90 dark:text-zinc-200 dark:hover:bg-zinc-800"
            activeTriggerClassName="inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 pl-3 pr-2 text-sm font-medium text-white shadow-[0_10px_24px_-16px_rgba(37,99,235,0.95)] transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            badgeClassName="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold text-white"
          />
        </div>
      </div>
    </div>
  );
}
